import { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import { Select } from '@shared/widgets/select';
import { ColorsEnum, Icon, TablePaginationCountOfItems, Text } from '@shared/widgets';
import FiChevronIcon from '@assets/icons/fi-chevron.svg';
import ChevronIcon from '@assets/icons/chevron.svg';

interface IPaginationProps {
  className?: string;
  currentPage: number;
  totalItemCount: number;
  countItemPage: TablePaginationCountOfItems;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
  noSelect?: boolean;
}

export const Pagination = (props: IPaginationProps) => {
  const totalPages = useMemo(() => {
    if (props.totalItemCount < props.countItemPage) return 1;
    const result = Math.ceil(props.totalItemCount / props.countItemPage);
    if (isNaN(result)) return 1;
    return result;
  }, [props.totalItemCount, props.countItemPage]);

  useEffect(() => {
    if (totalPages < props.currentPage) props.onChangePage(1);
  }, [totalPages]);

  const onChangePage = (page: number) => props.onChangePage(page);

  const firstItemNumber = useMemo(() => {
    if (props.totalItemCount === 0) return 0;
    return props.currentPage == 1 ? 1 : (props.currentPage - 1) * props.countItemPage + 1;
  }, [props.currentPage, props.countItemPage, props.totalItemCount]);

  const lastItemNumber = useMemo(() => {
    const result = props.currentPage * props.countItemPage;
    if (props.totalItemCount < result) return props.totalItemCount;
    return result;
  }, [props.currentPage, props.totalItemCount, props.countItemPage]);

  const isVcfcPalette = PaletteUtils.isVcfcPalette;
  return (
    <div className={clsx('flex justify-between min-h-9', props.className)}>
      <div className='flex'>
        <Text className='my-auto text-nowrap' size='sm' color={ColorsEnum.Secondary}>
          Page {props.currentPage} of {totalPages}
        </Text>
        <div className='flex bg-white dark:bg-soft-black mx-4'>
          <div
            className={clsx(
              'flex items-center justify-center px-3 cursor-pointer',
              props.currentPage > 1 ? `${isVcfcPalette ? 'bg-primary' : 'bg-soft-black dark:bg-slate'}` : 'bg-light dark:bg-slate dark:opacity-60',
            )}
            onClick={() => props.currentPage > 1 && onChangePage(1)}
          >
            <Icon icon={<FiChevronIcon />} color={PaletteUtils.getColor('primary', 'white')} />
          </div>
          <div
            className={clsx(
              'flex items-center px-3 cursor-pointer',
              props.currentPage > 1 ? 'text-slate dark:text-white dark:opacity-80' : 'text-almost-light-gray',
            )}
            onClick={() => props.currentPage > 1 && onChangePage(props.currentPage - 1)}
          >
            <Icon icon={<ChevronIcon />} />
          </div>
          <p className='text-nowrap my-auto px-3 text-slate dark:text-white text-sm cursor-pointer'>{props.currentPage}</p>
          <div
            className={clsx(
              'flex items-center px-3 cursor-pointer',
              props.currentPage + 1 <= totalPages ? 'text-slate dark:text-white dark:opacity-80' : 'text-almost-light-gray',
            )}
            onClick={() => props.currentPage + 1 <= totalPages && onChangePage(props.currentPage + 1)}
          >
            <Icon icon={<ChevronIcon />} className='rotate-180' />
          </div>
          <div
            className={clsx(
              'flex items-center justify-center px-3 cursor-pointer',
              props.currentPage + 1 <= totalPages
                ? `${isVcfcPalette ? 'bg-primary' : 'bg-soft-black dark:bg-slate'}`
                : 'bg-light dark:bg-slate dark:opacity-60',
            )}
            onClick={() => props.currentPage + 1 <= totalPages && onChangePage(totalPages)}
          >
            <Icon icon={<FiChevronIcon />} className='rotate-180' color={PaletteUtils.getColor('primary', 'white')} />
          </div>
        </div>
        <Text className='my-auto text-nowrap' size='sm' color={ColorsEnum.Secondary}>
          {firstItemNumber}-{lastItemNumber} results of {props.totalItemCount} shown
        </Text>
      </div>

      {props.noSelect ? (
        ''
      ) : (
        <div className='flex'>
          <Text className='my-auto mr-4 text-nowrap' size='sm' color={ColorsEnum.Secondary}>
            Rows per page
          </Text>
          <Select
            items={Object.values(TablePaginationCountOfItems)
              .filter((x) => Number.isInteger(x))
              .map((x) => x.toString())}
            nullValue={{ isEnable: false }}
            value={props.countItemPage.toString()}
            onChange={(value) => {
              const result = parseInt(value!);
              props.onChangePage(1);
              props.onChangeCountItemPage(result);
            }}
          />
        </div>
      )}
    </div>
  );
};
